// =================================================================================================
// IMPORTS
// =================================================================================================
import React, { useState, useEffect } from 'react';
import API from '../../services/api';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';

// =================================================================================================
// COMPONENT DEFINITION
// =================================================================================================
const Changelog = () => {
    // =============================================================================================
    // STATE MANAGEMENT & HOOKS
    // =============================================================================================
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeEntry, setActiveEntry] = useState(null);
    const [highlightedEntry, setHighlightedEntry] = useState(null);

    // =============================================================================================
    // EFFECTS
    // =============================================================================================
    useEffect(() => {
        const fetchChangelogEntries = async () => {
            try {
                const response = await API.get('changelog/entries/');
                setEntries(response.data);
                if (response.data.length > 0) {
                    setActiveEntry(response.data[0].id);
                }
            } catch (err) {
                console.error("Failed to fetch changelog entries:", err);
                setError("Failed to load changelog. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        fetchChangelogEntries();
    }, []);

    // This effect adds a global click listener to remove the highlight when the user clicks anywhere outside of a changelog entry.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (highlightedEntry && !event.target.closest('.group')) {
                setHighlightedEntry(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [highlightedEntry]);

    // =============================================================================================
    // EVENT HANDLERS & HELPERS
    // =============================================================================================
    const toggleDetails = (id) => {
        setActiveEntry(activeEntry === id ? null : id);
    };

    const copyLink = (id, e) => {
        e.stopPropagation();
        const url = `${window.location.origin}${window.location.pathname}#${id}`;
        navigator.clipboard.writeText(url);
        const tooltip = e.currentTarget.querySelector('.tooltip');
        if (tooltip) {
            tooltip.textContent = 'Copied!';
            setTimeout(() => {
                tooltip.textContent = 'Copy Link';
            }, 1500);
        }
    };

    const handleArchiveClick = (e, entryId) => {
        e.preventDefault();
        const targetElement = document.getElementById(`entry-${entryId}`);
        if (targetElement) {
            setHighlightedEntry(entryId); // Set highlight, which stays.
            setActiveEntry(entryId);      // Ensure the details are open.

            const offset = 100;
            const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
                top: elementPosition - offset,
                behavior: 'smooth'
            });
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    // =============================================================================================
    // JSX RENDERING
    // =============================================================================================
    return (
        <>
            {/* <style>{`
                .changelog-details { max-height: 0; overflow: hidden; transition: max-height 0.5s ease-in-out, opacity 0.3s ease; opacity: 0; }
                .changelog-details.active { max-height: 1000px; opacity: 1; }
                .tooltip { position: absolute; background-color: #1F1F1F; color: #D9D9D9; padding: 4px 8px; border-radius: 8px; font-size: 12px; white-space: nowrap; opacity: 0; visibility: hidden; transition: opacity 0.2s; z-index: 10; bottom: 125%; left: 50%; transform: translateX(-50%); }
                .copy-btn:hover .tooltip, .toggle-btn:hover .tooltip { opacity: 1; visibility: visible; }
                .rounded-container { border-radius: 12px; }
                .rounded-btn { border-radius: 12px; }
                .changelog-header.highlight { background-color: #0ea5e9; color: white; padding: 2px 6px; border-radius: 6px; display: inline-block; }
                .dark .changelog-header.highlight { background-color: #38bdf8; }
                
                .changelog-details ul { list-style: none; padding-left: 0; }
                .changelog-details li { position: relative; padding-left: 1rem; margin-bottom: 0.5rem; }
                .changelog-details li::before { content: ''; position: absolute; left: 0; top: 0.6em; transform: translateY(-50%); width: 6px; height: 6px; border-radius: 9999px; background-color: #9ca3af; }
                .dark .changelog-details li::before { background-color: #6b7280; }
            `}</style> */}

            <style>{`
                .changelog-details { max-height: 0; overflow: hidden; transition: max-height 0.5s ease-in-out, opacity 0.3s ease; opacity: 0; }
                .changelog-details.active { max-height: 5000px; opacity: 1; }
                .tooltip { position: absolute; background-color: #1F1F1F; color: #D9D9D9; padding: 4px 8px; border-radius: 8px; font-size: 12px; white-space: nowrap; opacity: 0; visibility: hidden; transition: opacity 0.2s; z-index: 10; bottom: 125%; left: 50%; transform: translateX(-50%); }
                .copy-btn:hover .tooltip, .toggle-btn:hover .tooltip { opacity: 1; visibility: visible; }
                .rounded-container { border-radius: 12px; }
                .rounded-btn { border-radius: 12px; }
                .changelog-header.highlight { background-color: #0ea5e9; color: white; padding: 2px 6px; border-radius: 6px; display: inline-block; }
                .dark .changelog-header.highlight { background-color: #38bdf8; }
            `}</style>

            <div className="bg-white dark:bg-[#141414] text-black dark:text-[#D9D9D9] transition-colors duration-300 min-h-screen flex flex-col scroll-smooth">
                <Navbar />
                <main className="flex-grow pt-24 pb-20 px-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex justify-between items-center mb-8">
                            <div className="flex flex-col gap-1">
                                <h1 className="text-[20px] font-semibold text-gray-900 dark:text-gray-100 leading-[28px]">Changelog</h1>
                                <p className="text-[14px] text-gray-500 dark:text-gray-400 leading-[20px]">
                                    Track the latest updates and improvements from the Trueway team
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-8">
                            <div className="flex-1">
                                <div className="space-y-8">
                                    {loading && <p className="text-gray-500 dark:text-gray-400">Loading changes...</p>}
                                    {error && <p className="text-red-500">{error}</p>}
                                    {!loading && !error && entries.length === 0 && (
                                        <div className="my-20">
                                            <div className="flex items-start gap-3">
                                                <div className="w-10 h-10 bg-gray-100 dark:bg-[#252525] rounded-full flex items-center justify-center flex-shrink-0">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6"><path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" /></svg>
                                                </div>
                                                <div>
                                                    <h3 className="text-base font-medium text-gray-800 dark:text-[#EDEDED]">No changelogs yet</h3>
                                                    <p className="text-sm text-gray-500 dark:text-[#9A9A9A] mt-1">We'll share updates here when they're available</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {entries.map(entry => (
                                        <div key={entry.id} id={`entry-${entry.id}`} className="bg-white dark:bg-[#1A1A1A] rounded-container border border-[#EAEAEA] dark:border-[#2D2D2D] p-4 group">
                                            <div className="flex flex-col sm:flex-row gap-4">
                                                <div className="w-full sm:w-32 flex-shrink-0">
                                                    <div className="text-[14px] bg-[#F5F8FF] dark:bg-[#1F2A47] text-[#2E5EE7] dark:text-[#5C89FF] rounded-container px-4 py-2 font-medium text-center">
                                                        {formatDate(entry.published_date)}
                                                    </div>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <h3 className={`text-[15px] font-medium text-gray-900 dark:text-gray-100 leading-6 changelog-header transition-all duration-300 ${highlightedEntry === entry.id ? 'highlight' : ''}`}>
                                                                {entry.title}
                                                            </h3>
                                                            {/* <div className={`changelog-details mt-3 ${activeEntry === entry.id ? 'active' : ''}`}
                                                                dangerouslySetInnerHTML={{ __html: entry.content }}>
                                                            </div> */}
                                                            <div className={`changelog-details mt-3 ${activeEntry === entry.id ? 'active' : ''}`}>
                                                                <article
                                                                    className="
                                                                    prose prose-slate dark:prose-invert max-w-none
                                                                    prose-headings:scroll-mt-24
                                                                    prose-a:underline prose-a:decoration-sky-500 hover:prose-a:text-sky-600
                                                                    prose-img:rounded-xl
                                                                    prose-pre:rounded-xl prose-pre:p-4
                                                                    prose-li:my-1
                                                                    marker:text-current
                                                                    "
                                                                    dangerouslySetInnerHTML={{ __html: entry.content }}
                                                                />
                                                             </div>
                                                        </div>
                                                        <div className="flex items-end">
                                                            <button onClick={(e) => copyLink(`entry-${entry.id}`, e)} className="copy-btn relative mr-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-btn">
                                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                                                                </svg>
                                                                <span className="tooltip">Copy Link</span>
                                                            </button>
                                                            <button onClick={() => toggleDetails(entry.id)} className="toggle-btn relative text-gray-400 dark:text-gray-500 p-1 -mr-1 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors rounded-btn">
                                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={`transition-transform duration-300 ${activeEntry === entry.id ? 'rotate-180' : ''}`}>
                                                                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                                <span className="tooltip">Toggle Details</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="md:w-64 shrink-0">
                                <div className="sticky top-20">
                                    <h3 className="text-lg font-semibold text-black dark:text-white mb-4">Archive</h3>
                                    {!loading && !error && entries.length === 0 && (
                                        <p className="text-sm text-gray-500 dark:text-[#9A9A9A] my-1">No changelogs yet</p>
                                    )}
                                    <ul className="space-y-2">
                                        {entries.map(entry => (
                                            <li key={`archive-${entry.id}`}>
                                                <a href={`#entry-${entry.id}`} onClick={(e) => handleArchiveClick(e, entry.id)} className="text-sm text-gray-600 dark:text-[#A3A3A3] hover:text-sky-600 dark:hover:text-[#7DD3FC] transition-colors duration-200">
                                                    {formatDate(entry.published_date)}: {entry.title}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        </>
    );
};

export default Changelog;